import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent, RefObject } from "react";
import { api, errorText } from "../../api/client";
import { useStore } from "../store";
import { kidStyle } from "../kid-style";
import { tapFeedback } from "../feedback";
import { GoalStepper } from "../components/GoalStepper";
import { SoundToggle } from "../components/SoundToggle";
import { NotificationsSection } from "../components/NotificationsSection";
import { AppearanceSection } from "../components/AppearanceSection";
import { KidAvatar } from "../components/KidAvatar";
import { PhotoPicker } from "../components/PhotoPicker";
import { Reveal } from "../components/Reveal";
import { Emoji } from "../components/Emoji";
import { SectionTitle } from "../components/SectionTitle";
import { KID_COLOURS, defaultColour } from "../../shared/palette";
import type { Kid, KidSummary } from "../../shared/types";

/** What the App section opens, on top of Settings. */
interface AppActions {
  onInstallGuide: () => void;
  onWatchIntro: () => void;
}

export function Settings(actions: AppActions) {
  const { snapshot } = useStore();
  if (!snapshot) return null;
  return snapshot.me.role === "adult" ? <AdultSettings {...actions} /> : <ChildSettings {...actions} />;
}

function ChildSettings(actions: AppActions) {
  const { snapshot } = useStore();
  const session = snapshot?.me;
  const me = session?.role === "child" ? snapshot?.kids.find((k) => k.id === session.kidId) : undefined;

  return (
    <main className="screen screen--settings">
      <h1 className="title">
        <Emoji>⚙️</Emoji>Settings
      </h1>
      <SignedIn kid={me} />
      <DeviceSettings {...actions} />
    </main>
  );
}

/**
 * Who this phone is signed in as, and the way out. A grown-up sees their own full address (the
 * session's, so a kid's phone never holds one); a kid sees their own avatar and name.
 */
function SignedIn({ kid }: { kid?: Kid }) {
  const { snapshot, logout } = useStore();
  const me = snapshot?.me;
  if (!me) return null;
  const owner = me.role === "adult" && snapshot.parents.some((p) => p.isAdmin && p.email === me.email);

  return (
    <section
      className={kid ? "signed-in signed-in--kid" : "signed-in"}
      style={kid ? kidStyle(kid.colour) : undefined}
      aria-labelledby="signed-in-title"
    >
      <h2 className="signed-in__title" id="signed-in-title">
        <span aria-hidden="true">👋</span> Signed in as
      </h2>
      <div className="signed-in__who">
        {kid && <KidAvatar kid={kid} size="md" />}
        {me.role === "adult" ? (
          // A long address breaks after the @ before it breaks anywhere else.
          <span className="signed-in__name signed-in__name--email ph-no-capture">
            {me.email.split("@")[0]}@<wbr />
            {me.email.split("@").slice(1).join("@")}
          </span>
        ) : (
          <span className="signed-in__name">{kid?.name ?? "A kid"}</span>
        )}
      </div>
      <div className="signed-in__foot">
        <span className="signed-in__badges">
          <span className="role-badge">{me.role === "adult" ? "Grown-up" : "Kid"}</span>
          {owner && <span className="role-badge">Owner</span>}
        </span>
        <button className="button button--small" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </div>
    </section>
  );
}

function AdultSettings(actions: AppActions) {
  const { snapshot } = useStore();
  if (!snapshot) return null;

  return (
    <main className="screen screen--settings">
      <h1 className="title">
        <Emoji>⚙️</Emoji>Settings
      </h1>
      <SignedIn />

      <section className="section">
        <SectionTitle emoji="👧">Kids</SectionTitle>
        {snapshot.kids.length === 0 ? (
          <p className="hint">No kids yet. Add one below.</p>
        ) : (
          <p className="hint">
            A day that closes at or above a kid's goal earns a star. A goal change counts from today.
          </p>
        )}
        <ul className="list">
          {snapshot.kids.map((kid) => (
            <KidRow key={kid.id} kid={kid} />
          ))}
        </ul>
        <AddKid nextColour={defaultColour(snapshot.kids.length)} startOpen={snapshot.kids.length === 0} />
      </section>

      <section className="section">
        <SectionTitle emoji="🧑‍🤝‍🧑">Grown-ups</SectionTitle>
        <p className="hint">Anyone listed here can sign in with their email and change points.</p>
        <Parents />
      </section>

      <NotificationsSection />
      <DeviceSettings {...actions} />
    </main>
  );
}

type RowMode = "view" | "edit" | "remove";

/**
 * Everything about one kid in one card: photo (tap it to change it) and name on their colour,
 * their goal, their sign-in code on demand, and Edit (name, colour and goal, saved together) /
 * Remove.
 */
function KidRow({ kid }: { kid: KidSummary }) {
  const { mintKidCode, editKid, setGoal, removeKid } = useStore();
  // undefined = not looked up yet, null = looked up and none minted.
  const [code, setCode] = useState<string | null | undefined>(undefined);
  const codeText = useRef<HTMLSpanElement>(null);
  const [mode, setMode] = useState<RowMode>("view");
  const [picking, setPicking] = useState(false);
  const closePicker = useCallback(() => setPicking(false), []);
  const [name, setName] = useState(kid.name);
  const [colour, setColour] = useState(kid.colour);
  const [goal, setGoalDraft] = useState(kid.goal);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [typed, setTyped] = useState("");

  async function attempt(action: () => Promise<void>, fallback: string) {
    setBusy(true);
    setErr(null);
    try {
      await action();
    } catch (e) {
      setErr(errorText(e, fallback));
    } finally {
      setBusy(false);
    }
  }

  const show = () => attempt(async () => setCode((await api.getKidCode(kid.id)).code), "Couldn't load the code.");

  // A code on screen is re-read when the app comes back, so one the other grown-up replaced
  // never sits here looking current.
  const shown = code !== undefined;
  useEffect(() => {
    if (!shown) return;
    const reread = () => {
      if (document.visibilityState !== "visible") return;
      api
        .getKidCode(kid.id)
        .then((r) => setCode(r.code))
        .catch(() => undefined);
    };
    document.addEventListener("visibilitychange", reread);
    window.addEventListener("focus", reread);
    return () => {
      document.removeEventListener("visibilitychange", reread);
      window.removeEventListener("focus", reread);
    };
  }, [shown, kid.id]);
  const regenerate = () => {
    tapFeedback(25);
    return attempt(async () => setCode(await mintKidCode(kid.id)), "Couldn't make a code.");
  };

  function startEdit() {
    setName(kid.name);
    setColour(kid.colour);
    setGoalDraft(kid.goal);
    setErr(null);
    setMode("edit");
  }

  async function saveEdit(e: FormEvent) {
    e.preventDefault();
    await attempt(async () => {
      // Only what changed is sent: a goal change counts from today, so an untouched goal stays put.
      if (name.trim() !== kid.name || colour !== kid.colour) await editKid(kid.id, name.trim(), colour);
      if (goal !== kid.goal) await setGoal(kid.id, goal);
      setMode("view");
    }, "Couldn't save the changes.");
  }

  // On success this row unmounts with the kid, so there is no state to reset.
  const remove = () => attempt(() => removeKid(kid.id, typed), "Couldn't remove them.");
  // Typing the name, not one more tap, is what stops a stray touch or a kid removing a kid.
  const nameTyped = typed.trim().toLowerCase() === kid.name.trim().toLowerCase();
  function startRemove() {
    setTyped("");
    setErr(null);
    setMode("remove");
  }

  return (
    // While editing, the card already wears the colour being picked.
    <li className="list__item kid-row" style={kidStyle(mode === "edit" ? colour : kid.colour)}>
      {mode === "edit" ? (
        <form className="kid-row__edit" onSubmit={(e) => void saveEdit(e)}>
          <label className="field">
            <span className="field__label">Name</span>
            <input
              className="input"
              type="text"
              autoComplete="off"
              autoCapitalize="words"
              maxLength={24}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
          <fieldset className="field swatches">
            <legend className="field__label">Colour</legend>
            {KID_COLOURS.map((c) => (
              <label key={c} className="swatch" style={kidStyle(c)}>
                <input
                  type="radio"
                  name={`colour-${kid.id}`}
                  value={c}
                  checked={colour === c}
                  onChange={() => setColour(c)}
                />
                <span className="visually-hidden">{c}</span>
              </label>
            ))}
          </fieldset>
          <div className="kid-goal">
            <span className="kid-goal__label">
              <Emoji>🎯</Emoji>Goal
            </span>
            <GoalStepper name={name.trim() || kid.name} goal={goal} busy={busy} onChange={setGoalDraft} />
          </div>
          <div className="kid-row__actions">
            <button className="button button--small" type="submit" disabled={busy || !name.trim()}>
              {busy ? "Saving…" : "Save"}
            </button>
            <button className="button button--small" type="button" disabled={busy} onClick={() => setMode("view")}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <span className="kid-row__who">
          <button
            className="kid-row__photo"
            type="button"
            disabled={busy}
            aria-label={`${kid.photoUpdatedAt === null ? "Add" : "Change"} photo of ${kid.name}`}
            onClick={() => setPicking(true)}
          >
            <KidAvatar kid={kid} size="md" />
            <svg className="kid-row__camera" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 4.5 7.6 6.5H5A2.5 2.5 0 0 0 2.5 9v8A2.5 2.5 0 0 0 5 19.5h14a2.5 2.5 0 0 0 2.5-2.5V9A2.5 2.5 0 0 0 19 6.5h-2.6L15 4.5Z" />
              <circle cx="12" cy="13" r="3.6" />
            </svg>
          </button>
          <span className="kid-row__name">{kid.name}</span>
        </span>
      )}

      {mode === "view" && (
        <p className="kid-goal__read">
          <Emoji>🎯</Emoji>Goal {kid.goal}
        </p>
      )}

      {mode === "view" &&
        (code === undefined ? (
          <button className="button button--small" type="button" disabled={busy} onClick={() => void show()}>
            Show sign-in code
          </button>
        ) : (
          <div className="kid-row__code">
            {code ? (
              <>
                <span
                  ref={codeText}
                  className="code ph-no-capture"
                  aria-label={`Sign-in code ${code.split("").join(" ")}`}
                >
                  {code}
                </span>
                <CopyCode code={code} name={kid.name} text={codeText} />
              </>
            ) : (
              <span className="hint">No code yet</span>
            )}
            <button className="button button--small" type="button" disabled={busy} onClick={() => void regenerate()}>
              {code ? "New code" : "Make a code"}
            </button>
          </div>
        ))}

      {mode === "view" && (
        <div className="kid-row__actions">
          <button className="button button--small" type="button" disabled={busy} onClick={startEdit}>
            Edit
          </button>
          <button className="button button--small" type="button" disabled={busy} onClick={startRemove}>
            Remove
          </button>
        </div>
      )}

      {mode === "remove" && (
        <form
          className="kid-row__confirm"
          onSubmit={(e) => {
            e.preventDefault();
            if (nameTyped) void remove();
          }}
        >
          <p className="hint">Remove {kid.name} and every point, star and photo they have? This can't be undone.</p>
          <label className="field">
            <span className="field__label">Type {kid.name} to confirm</span>
            <input
              className="input"
              type="text"
              autoComplete="off"
              autoCapitalize="words"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>
          <div className="kid-row__actions">
            <button className="button button--small" type="submit" disabled={busy || !nameTyped}>
              {busy ? "Removing…" : `Remove ${kid.name}`}
            </button>
            <button className="button button--small" type="button" disabled={busy} onClick={() => setMode("view")}>
              Keep {kid.name}
            </button>
          </div>
        </form>
      )}

      {err && <p className="error">{err}</p>}
      {picking && <PhotoPicker kid={kid} onClose={closePicker} />}
    </li>
  );
}

/**
 * Copies a kid's code to send to their device. Where the clipboard is missing (an old browser,
 * a page not served over HTTPS) or refuses, the code is selected instead, ready for the phone's
 * own Copy.
 */
function CopyCode({ code, name, text }: { code: string; name: string; text: RefObject<HTMLSpanElement | null> }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copy() {
    tapFeedback();
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      const el = text.current;
      const selection = window.getSelection();
      if (!el || !selection) return;
      const range = document.createRange();
      range.selectNodeContents(el);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  return (
    <>
      <button
        className="button button--small kid-row__copy"
        type="button"
        aria-label={`Copy ${name}'s code`}
        onClick={() => void copy()}
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <span className="visually-hidden" role="status">
        {copied ? `Copied ${name}'s code` : ""}
      </span>
    </>
  );
}

/**
 * "+ Add a kid", which opens into the form. Shut until asked for, so it does not dominate the
 * page under the kids already there; open from the start when there are none, so first-run setup
 * is obvious.
 */
function AddKid({ nextColour, startOpen }: { nextColour: string; startOpen: boolean }) {
  const { createKid } = useStore();
  const [open, setOpen] = useState(startOpen);
  const opener = useRef<HTMLButtonElement>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  // null = follow the suggested next colour until the parent picks one.
  const [picked, setPicked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const colour = picked ?? nextColour;

  function show() {
    setOpen(true);
    // The input is inert until this render lands, so focus it on the next frame.
    requestAnimationFrame(() => nameInput.current?.focus());
  }

  function hide() {
    setOpen(false);
    setName("");
    setPicked(null);
    setErr(null);
    requestAnimationFrame(() => opener.current?.focus());
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await createKid(name.trim(), colour);
      tapFeedback(25);
      hide();
    } catch (e2) {
      setErr(errorText(e2, "Couldn't add that kid."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="collapsible">
      <Reveal open={!open}>
        <button ref={opener} className="button button--wide" type="button" aria-expanded={false} onClick={show}>
          + Add a kid
        </button>
      </Reveal>
      <Reveal open={open}>
        <form className="stack card" onSubmit={(e) => void submit(e)}>
          <h3 className="card__title">Add a kid</h3>
          <label className="field">
            <span className="field__label">Name</span>
            <input
              ref={nameInput}
              className="input"
              type="text"
              autoComplete="off"
              autoCapitalize="words"
              maxLength={24}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
          <fieldset className="field swatches">
            <legend className="field__label">Colour</legend>
            {KID_COLOURS.map((c) => (
              <label key={c} className="swatch" style={kidStyle(c)}>
                <input type="radio" name="colour" value={c} checked={colour === c} onChange={() => setPicked(c)} />
                <span className="visually-hidden">{c}</span>
              </label>
            ))}
          </fieldset>
          {err && <p className="error">{err}</p>}
          <div className="form-actions">
            <button className="button button--primary" type="submit" disabled={busy || !name.trim()}>
              {busy ? "Adding…" : "Add kid"}
            </button>
            <button className="button" type="button" disabled={busy} onClick={hide}>
              Cancel
            </button>
          </div>
        </form>
      </Reveal>
    </div>
  );
}

function Parents() {
  const { snapshot, removeParent } = useStore();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!snapshot) return null;

  async function attempt(action: () => Promise<void>, fallback: string) {
    setBusy(true);
    setErr(null);
    try {
      await action();
    } catch (e) {
      setErr(errorText(e, fallback));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ul className="list">
        {snapshot.parents.map((p) => (
          <li key={p.email} className="list__item parent-row">
            <span className="parent-row__email ph-no-capture">{p.email}</span>
            {p.isAdmin ? (
              <span className="hint">Admin</span>
            ) : snapshot.me.role === "adult" && p.email === snapshot.me.email ? (
              <span className="hint">You</span>
            ) : (
              <span className="parent-row__actions">
                <ResendInvite email={p.email} />
                <button
                  className="button button--small"
                  type="button"
                  disabled={busy}
                  onClick={() => void attempt(() => removeParent(p.email), "Couldn't remove them.")}
                >
                  Remove
                </button>
              </span>
            )}
          </li>
        ))}
      </ul>
      {err && <p className="error">{err}</p>}
      <InviteGrownUp />
    </>
  );
}

/** "+ Invite a grown-up", which opens into the invite form, shut again once they are invited. */
function InviteGrownUp() {
  const { inviteParent } = useStore();
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const emailInput = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function show() {
    setOpen(true);
    // The input is inert until this render lands, so focus it on the next frame.
    requestAnimationFrame(() => emailInput.current?.focus());
  }

  function hide() {
    setOpen(false);
    setEmail("");
    setErr(null);
    requestAnimationFrame(() => opener.current?.focus());
  }

  async function invite(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await inviteParent(email.trim());
      tapFeedback(25);
      hide();
    } catch (e2) {
      setErr(errorText(e2, "Couldn't add that email."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="collapsible">
      <Reveal open={!open}>
        <button ref={opener} className="button button--wide" type="button" aria-expanded={false} onClick={show}>
          + Invite a grown-up
        </button>
      </Reveal>
      <Reveal open={open}>
        <form className="stack card" onSubmit={(e) => void invite(e)}>
          <label className="field">
            <span className="field__label">Add a grown-up by email</span>
            <input
              ref={emailInput}
              className="input"
              type="email"
              inputMode="email"
              autoComplete="off"
              placeholder="them@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          {err && <p className="error">{err}</p>}
          <div className="form-actions">
            <button className="button button--primary" type="submit" disabled={busy || !email.includes("@")}>
              {busy ? "Inviting…" : "Invite"}
            </button>
            <button className="button" type="button" disabled={busy} onClick={hide}>
              Cancel
            </button>
          </div>
          <p className="hint">We'll email them an invite with a link to Pointz. They sign in with this address.</p>
        </form>
      </Reveal>
    </div>
  );
}

type SendState = "idle" | "sending" | "sent" | "failed";

/**
 * Emails a listed grown-up their invite again: for anyone added before invites were emailed, or
 * who lost theirs. It is the same call as adding them, which re-sends to someone already listed.
 */
function ResendInvite({ email }: { email: string }) {
  const { inviteParent } = useStore();
  const [state, setState] = useState<SendState>("idle");

  useEffect(() => {
    if (state !== "sent" && state !== "failed") return;
    const timer = window.setTimeout(() => setState("idle"), 2000);
    return () => window.clearTimeout(timer);
  }, [state]);

  async function resend() {
    tapFeedback();
    setState("sending");
    try {
      await inviteParent(email);
      setState("sent");
    } catch {
      setState("failed");
    }
  }

  const label = { idle: "Resend invite", sending: "Sending…", sent: "Sent", failed: "Didn't send" }[state];
  return (
    <>
      <button
        className="button button--small parent-row__resend"
        type="button"
        disabled={state === "sending"}
        aria-label={`Resend invite to ${email}`}
        onClick={() => void resend()}
      >
        {label}
      </button>
      <span className="visually-hidden" role="status">
        {state === "sent" ? "Invite sent" : state === "failed" ? "The invite didn't send" : ""}
      </span>
    </>
  );
}

/** Sound, install, the intro and appearance: they belong to this phone, not the household, so kids get them too. */
function DeviceSettings({ onInstallGuide, onWatchIntro }: AppActions) {
  return (
    <>
      <section className="section">
        <SectionTitle emoji="🔊">Sound</SectionTitle>
        <SoundToggle />
      </section>
      <section className="section">
        <SectionTitle emoji="📱">App</SectionTitle>
        <button className="button button--ghost" type="button" onClick={onInstallGuide}>
          Install on your phone
        </button>
        <button className="button button--ghost" type="button" onClick={onWatchIntro}>
          Watch the intro
        </button>
      </section>
      <AppearanceSection />
    </>
  );
}
